import React, { useEffect, useState, Fragment } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetPublicApartmentUnitById, UpdatePublicApartmentUnit, PublicApartmentUnit, PublicApartmentOccupant, DeletePublicApartmentUnit } from "./DataServices/PublicApartmentUnitsService";
import { ArrowLeftIcon, BuildingOfficeIcon, PlusIcon, TrashIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { saveListing, saveRentedListing } from "./DataServices/ApartmentsListingsDataServices";
import { baseUrl, getTokenFromLocalStorage } from "./DataServices/Configuration";
import Notification from "./Notification";

const ExtractedApartmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apartment, setApartment] = useState<Partial<PublicApartmentUnit> | null>(null);
  const [occupants, setOccupants] = useState<Partial<PublicApartmentOccupant>[]>([]);

  const [listingModalOpen, setListingModalOpen] = useState(false);
  const [moveOutModalOpen, setMoveOutModalOpen] = useState(false);
  const [isVacant, setIsVacant] = useState(false);
  const [moveInDate, setMoveInDate] = useState("");
  const [moveOutDate, setMoveOutDate] = useState("");

  const [openNotification, setOpenNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationMessageType, setNotificationMessageType] = useState("success");

  useEffect(() => {
    if (!id) return;
    const fetchApt = async () => {
      try {
        const data = await GetPublicApartmentUnitById(id);
        setApartment(data);
        setOccupants(data.occupants || []);
      } catch (error) {
        console.error("Failed to load apartment", error);
      } finally {
        setLoading(false);
      }
    };
    fetchApt();
  }, [id]);

  const saveTenants = async (listingId: string, occupantsToSave: any[]) => {
    const token = await getTokenFromLocalStorage();
    for (const occ of occupantsToSave) {
      const payload = {
        id: crypto.randomUUID(),
        listingId: listingId,
        name: occ.name,
        email: occ.email || "",
        phone: occ.phone || "",
        isGuarantor: occ.isCosigner,
        isReceiver: false,
        showingAppointmentStarted: false,
      };
      await fetch(`${baseUrl}/api/Appointments/TenantContact`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
    }
  };

  const submitToListing = async () => {
    if (!id || !apartment) return;
    if (!isVacant && !moveInDate) {
      setNotificationMessage("Please provide either Is Vacant or Move In Date.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
      return;
    }
    
    setSaving(true);
    try {
      const listingId = crypto.randomUUID();
      const payload = [{
        id: listingId,
        idApartment: crypto.randomUUID(),
        title: apartment.apartmentTitle,
        zillowTitle: apartment.zillowTitle || apartment.apartmentTitle,
        description: apartment.description || "",
        building: apartment.building,
        line: apartment.line || "",
        floor: apartment.floor || 0,
        rent: apartment.rentalPrice || 0,
        currentRent: apartment.currentRent || 0,
        numOfBedrooms: apartment.bedrooms || 0,
        publicApartmentUnitId: apartment.id,
        isVacant: isVacant,
        moveInDate: moveInDate || null,
        accessCode: apartment.accessCode || "",
        appfolioLink: apartment.appfolioLink || "",
        youTubeLink: apartment.youTubeUrl || ""
      }];

      await saveListing(payload as any);
      await saveTenants(listingId, occupants);

      setListingModalOpen(false);
      setNotificationMessage("Successfully moved to Listings.");
      setNotificationMessageType("success");
      setOpenNotification(true);
      setTimeout(() => {
        setOpenNotification(false);
        navigate("/extracted-apartments");
      }, 1500);
    } catch (e) {
      console.error(e);
      setNotificationMessage("Failed to move to listings.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const submitToMoveOut = async () => {
    if (!id || !apartment) return;
    if (!moveOutDate) {
      setNotificationMessage("Please provide Move Out Date.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
      return;
    }
    if (hasMissingMandatoryInfo()) {
      setNotificationMessage("All occupants must have their required fields filled in before proceeding to move-out.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 4000);
      return;
    }
    
    setSaving(true);
    try {
      const listingId = crypto.randomUUID();
      const payload = [{
        id: listingId,
        idApartment: crypto.randomUUID(),
        title: apartment.apartmentTitle,
        zillowTitle: apartment.zillowTitle || apartment.apartmentTitle,
        description: apartment.description || "",
        building: apartment.building,
        line: apartment.line || "",
        floor: apartment.floor || 0,
        rent: apartment.rentalPrice || 0,
        currentRent: apartment.currentRent || 0,
        numOfBedrooms: apartment.bedrooms || 0,
        publicApartmentUnitId: apartment.id,
        isVacant: false,
        moveOutDate: moveOutDate,
        accessCode: apartment.accessCode || "",
        appfolioLink: apartment.appfolioLink || "",
        youTubeLink: apartment.youTubeUrl || "",
        occupants: occupants.map(occ => ({
          name: occ.name || "",
          email: occ.email || "",
          phone: occ.phone || ""
        }))
      }];

      await saveRentedListing(payload as any);
      await saveTenants(listingId, occupants);

      setMoveOutModalOpen(false);
      setNotificationMessage("Successfully moved to Move-outs.");
      setNotificationMessageType("success");
      setOpenNotification(true);
      setTimeout(() => {
        setOpenNotification(false);
        navigate("/extracted-apartments");
      }, 1500);
    } catch (e) {
      console.error(e);
      setNotificationMessage("Failed to move to move-outs.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!id || !apartment) return;

    setSaving(true);
    try {
      const payload = {
        ...apartment,
        occupants: occupants,
      };
      await UpdatePublicApartmentUnit(id, payload);
      setNotificationMessage("Saved successfully!");
      setNotificationMessageType("success");
      setOpenNotification(true);
      setTimeout(() => {
        setOpenNotification(false);
        navigate("/extracted-apartments");
      }, 1500);
    } catch (error) {
      console.error("Failed to save", error);
      setNotificationMessage("Failed to save changes.");
      setNotificationMessageType("error");
      setOpenNotification(true);
      setTimeout(() => setOpenNotification(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleAddOccupant = () => {
    setOccupants([
      ...occupants,
      {
        id: crypto.randomUUID(),
        name: "",
        email: "",
        phone: "",
        isCosigner: false,
      }
    ]);
  };

  const handleOccupantChange = (index: number, field: keyof PublicApartmentOccupant, value: any) => {
    const updated = [...occupants];
    updated[index] = { ...updated[index], [field]: value };
    setOccupants(updated);
  };

  const handleRemoveOccupant = (index: number) => {
    const updated = [...occupants];
    updated.splice(index, 1);
    setOccupants(updated);
  };

  const hasMissingInfo = () =>
    occupants.some(occ => !occ.name?.trim() || !occ.email?.trim() || !occ.phone?.trim());

  const hasMissingMandatoryInfo = () =>
    occupants.some(occ => !occ.name?.trim() || !occ.email?.trim());

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!apartment) {
    return <div className="text-center mt-12">Apartment not found</div>;
  }

  return (
    <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <Notification
        isOpen={openNotification}
        message={notificationMessage}
        messageType={notificationMessageType}
        setIsOpen={setOpenNotification}
      />
      <button
        onClick={() => navigate("/extracted-apartments")}
        className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mb-6 transition-colors"
      >
        <ArrowLeftIcon className="mr-1 h-4 w-4" />
        Back to Extracted Data
      </button>

      {hasMissingInfo() && (
        <div className="mb-6 rounded-md bg-amber-50 border border-amber-200 p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-amber-800">Missing Tenant Information</h3>
              <p className="mt-1 text-sm text-amber-700">
                Some occupants have incomplete information. This is optional for listings but may be required for move-outs.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow-sm ring-1 ring-gray-900/5 sm:rounded-xl md:col-span-2">
        <div className="px-4 py-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-3">
              <BuildingOfficeIcon className="h-6 w-6 text-gray-400" />
              <h2 className="text-xl font-bold text-gray-900">Apartment Details</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
              <button
                type="button"
                onClick={() => setListingModalOpen(true)}
                className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Move to Listing
              </button>
              <button
                type="button"
                onClick={() => setMoveOutModalOpen(true)}
                className="inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Move to Move-out
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <label className="block text-sm font-medium leading-6 text-gray-900">Apartment Title *</label>
              <div className="mt-2">
                <input
                  type="text"
                  value={apartment.apartmentTitle || ""}
                  onChange={(e) => setApartment({ ...apartment, apartmentTitle: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label className="block text-sm font-medium leading-6 text-gray-900">Zillow Title</label>
              <div className="mt-2">
                <input
                  type="text"
                  value={apartment.zillowTitle || ""}
                  onChange={(e) => setApartment({ ...apartment, zillowTitle: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">Building</label>
              <div className="mt-2">
                <input
                  type="text"
                  value={apartment.building || ""}
                  onChange={(e) => setApartment({ ...apartment, building: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">Unit Name</label>
              <div className="mt-2">
                <input
                  type="text"
                  value={apartment.unitName || ""}
                  onChange={(e) => setApartment({ ...apartment, unitName: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">Rental Price</label>
              <div className="mt-2">
                <input
                  type="number"
                  value={apartment.rentalPrice || ""}
                  onChange={(e) => setApartment({ ...apartment, rentalPrice: Number(e.target.value) })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">Current Rent</label>
              <div className="mt-2">
                <input
                  type="number"
                  value={apartment.currentRent || ""}
                  onChange={(e) => setApartment({ ...apartment, currentRent: Number(e.target.value) })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">Bedrooms</label>
              <div className="mt-2">
                <input
                  type="number"
                  value={apartment.bedrooms || ""}
                  onChange={(e) => setApartment({ ...apartment, bedrooms: Number(e.target.value) })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>
            
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">Floor</label>
              <div className="mt-2">
                <input
                  type="number"
                  value={apartment.floor || ""}
                  onChange={(e) => setApartment({ ...apartment, floor: Number(e.target.value) })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">Line</label>
              <div className="mt-2">
                <input
                  type="text"
                  value={apartment.line || ""}
                  onChange={(e) => setApartment({ ...apartment, line: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium leading-6 text-gray-900">Access Code</label>
              <div className="mt-2">
                <input
                  type="text"
                  value={apartment.accessCode || ""}
                  onChange={(e) => setApartment({ ...apartment, accessCode: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label className="block text-sm font-medium leading-6 text-gray-900">Appfolio Link</label>
              <div className="mt-2">
                <input
                  type="url"
                  value={apartment.appfolioLink || ""}
                  onChange={(e) => setApartment({ ...apartment, appfolioLink: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="sm:col-span-3">
              <label className="block text-sm font-medium leading-6 text-gray-900">YouTube URL</label>
              <div className="mt-2">
                <input
                  type="url"
                  value={apartment.youTubeUrl || ""}
                  onChange={(e) => setApartment({ ...apartment, youTubeUrl: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>

            <div className="col-span-full">
              <label className="block text-sm font-medium leading-6 text-gray-900">Description</label>
              <div className="mt-2">
                <textarea
                  rows={3}
                  value={apartment.description || ""}
                  onChange={(e) => setApartment({ ...apartment, description: e.target.value })}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6 px-3"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-6 sm:p-8 border-t border-gray-900/10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Occupants (Tenants & Guarantors)</h2>
              <p className="mt-1 text-sm text-gray-500">Add or remove occupants associated with this extracted unit.</p>
            </div>
            <button
              onClick={handleAddOccupant}
              className="inline-flex items-center justify-center rounded-md bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 shadow-sm ring-1 ring-inset ring-blue-700/10"
            >
              <PlusIcon className="mr-1 h-4 w-4" /> Add Occupant
            </button>
          </div>

          <div className="space-y-4">
            {occupants.map((occ, idx) => (
              <div key={idx} className="relative bg-gray-50 p-4 rounded-lg border border-gray-200">
                <button
                  onClick={() => handleRemoveOccupant(idx)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
                <div className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-6 pr-8">
                  <div className="sm:col-span-3">
                    <label className="block text-xs font-medium text-gray-700">Name *</label>
                    <input
                      type="text"
                      value={occ.name || ""}
                      onChange={(e) => handleOccupantChange(idx, "name", e.target.value)}
                      className={`mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm px-2 ${!occ.name?.trim() ? 'ring-red-500' : 'ring-gray-300'}`}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="block text-xs font-medium text-gray-700">Email *</label>
                    <input
                      type="email"
                      value={occ.email || ""}
                      onChange={(e) => handleOccupantChange(idx, "email", e.target.value)}
                      className={`mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm px-2 ${!occ.email?.trim() ? 'ring-red-500' : 'ring-gray-300'}`}
                    />
                  </div>
                  <div className="sm:col-span-3">
                    <label className="block text-xs font-medium text-gray-700">Phone <span className="text-gray-400 font-normal">(Optional)</span></label>
                    <input
                      type="text"
                      value={occ.phone || ""}
                      onChange={(e) => handleOccupantChange(idx, "phone", e.target.value)}
                      className="mt-1 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm px-2"
                    />
                  </div>
                  <div className="sm:col-span-3 flex items-end pb-1.5">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={occ.isCosigner || false}
                        onChange={(e) => handleOccupantChange(idx, "isCosigner", e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                      />
                      <span className="text-sm text-gray-700">Is Cosigner / Guarantor</span>
                    </label>
                  </div>
                </div>
              </div>
            ))}
            {occupants.length === 0 && (
              <div className="text-center py-6 text-gray-500 text-sm border-2 border-dashed border-gray-300 rounded-lg">
                No occupants added.
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Listing Modal */}
      <Transition.Root show={listingModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setListingModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-0 text-center sm:items-center sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-t-2xl sm:rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full sm:my-8 sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Move to Listings
                      </Dialog.Title>
                      {hasMissingInfo() && (
                        <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-left">
                          <p className="text-sm text-amber-800 flex items-start gap-2">
                            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            Some occupants have incomplete infomation. You may still proceed with listings.
                          </p>
                        </div>
                      )}
                      <div className="mt-4 text-left">
                        <label className="flex items-center gap-2 cursor-pointer mb-4">
                          <input
                            type="checkbox"
                            checked={isVacant}
                            onChange={(e) => setIsVacant(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600"
                          />
                          <span className="text-sm font-medium text-gray-900">Is Vacant</span>
                        </label>
                        {!isVacant && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium leading-6 text-gray-900">Move In Date</label>
                            <input
                              type="date"
                              value={moveInDate}
                              onChange={(e) => setMoveInDate(e.target.value)}
                              className="mt-2 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      disabled={saving}
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 disabled:opacity-50"
                      onClick={submitToListing}
                    >
                      {saving ? "Saving..." : "Confirm"}
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={() => setListingModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Move Out Modal */}
      <Transition.Root show={moveOutModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setMoveOutModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-0 text-center sm:items-center sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-t-2xl sm:rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full sm:my-8 sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900">
                        Move to Move-outs
                      </Dialog.Title>
                      {hasMissingMandatoryInfo() ? (
                        <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-3 text-left">
                          <p className="text-sm text-red-800 flex items-start gap-2">
                            <ExclamationTriangleIcon className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                            Some occupants have incomplete information. Fill in mandatory details to proceed with move-outs.
                          </p>
                        </div>
                      ) : hasMissingInfo() ? (
                        <div className="mt-4 rounded-md bg-amber-50 border border-amber-200 p-3 text-left">
                          <p className="text-sm text-amber-800 flex items-start gap-2">
                            <ExclamationTriangleIcon className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            Some occupants have incomplete information. You may still proceed with move-outs.
                          </p>
                        </div>
                      ) : null}
                      <div className="mt-4 text-left">
                        <div className="mb-4">
                          <label className="block text-sm font-medium leading-6 text-gray-900">Move Out Date</label>
                          <input
                            type="date"
                            value={moveOutDate}
                            onChange={(e) => setMoveOutDate(e.target.value)}
                            className="mt-2 block w-full rounded-md border-0 py-1.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      disabled={saving || hasMissingMandatoryInfo()}
                      className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 sm:col-start-2 disabled:opacity-50"
                      onClick={submitToMoveOut}
                    >
                      {saving ? "Saving..." : "Confirm"}
                    </button>
                    <button
                      type="button"
                      disabled={saving}
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={() => setMoveOutModalOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default ExtractedApartmentDetail;
